import { createAsync, query, useNavigate } from "@solidjs/router";
import {
  Accessor,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  Suspense,
  createMemo,
  JSX,
} from "solid-js";
import { Product } from "~/server/types/search";
import { CategoryStripV2 } from "~/components/webstore_telescope/category_strip_v2";
import { CategoryCardV2 } from "~/components/webstore_telescope/category_card_v2";
import {
  getHomeV3RouteData,
  HomeV3RouteData,
  SectionData,
} from "./data/home_v3_route_data";
import { FullScreenLoaderTelescope } from "~/components/webstore_telescope/full_screen_loader";
import {
  PopularBrandsSectionHeader,
  SectionHeader,
} from "./fragments/section_header";
import { BrandGrid } from "./fragments/brand_grid";
import { CoinsBalanceDisplay } from "~/components/webstore_telescope/coins_balance_display";
import { HomeV3Provider } from "./context/home_v3_context";
import { useHomeV3 } from "./hooks/useHomeV3";
import { zIndexes } from "~/z_indexes";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { RNREvent } from "~/data/events";
import { BackgroundGradientV2 } from "~/components/webstore/background_gradient_v2";
import { CoinsTransactionsModalTelescope } from "~/components/webstore_telescope/coins_transactions_modal_telescope";
import { openModalV2, goBack } from "~/shared_states/modal";
import { useWebStoreContext } from "~/context/webstore_context";
import { CategoriesModal } from "~/components/webstore_telescope/categories_modal";
import { clientOnly } from "@solidjs/start";
import { isMobile } from "~/utils/platform";
import { HubbleBadge } from "~/components/webstore_telescope/hubble_badge";
import { BRANDS_COUNT, clientIdConfig } from "~/data/partner_config";
import RaisoniHeader from "./fragments/custom_rnr_components/raisoni_header";
import BuddydealsHeader from "./fragments/custom_rnr_components/buddydeals_header";
import NielsenHeader from "./fragments/custom_rnr_components/nielsen_header";

const getHomeV3RouteData$C = query(getHomeV3RouteData, "home_v3");

// client only
export default clientOnly(async () => ({ default: HomeTelescopeRnr }), {
  lazy: true,
});

export function HomeTelescopeRnr() {
  const fetchHomeSeparatorSections = true;
  const routeData: Accessor<HomeV3RouteData | undefined> = createAsync(
    () => getHomeV3RouteData$C({ fetchHomeSeparatorSections }),
    { deferStream: true }
  );

  return (
    <Suspense fallback={<FullScreenLoaderTelescope />}>
      <Show when={routeData()}>
        <HomeV3TelescopeContent
          routeData={routeData as Accessor<HomeV3RouteData>}
        />
      </Show>
    </Suspense>
  );
}

function HomeV3TelescopeContent(props: {
  routeData: Accessor<HomeV3RouteData>;
}) {
  const navigate = useNavigate();
  const homeV3Hook = useHomeV3(props.routeData);

  // Track if category strip is in sticky state (for mobile card/strip switching)
  const [isSticky, setIsSticky] = createSignal(false);
  let sentinelRef: HTMLDivElement | undefined;
  let categoryObserver: IntersectionObserver | undefined;

  const {
    refreshV4HomePage,
    setIsHeaderScrolledOut,
    storeState,
    getPartnerConfigV3,
    getClientId,
  } = useWebStoreContext();

  const onBrandClick = (brandKey: string) => {
    navigate(`/buy-gift-card/${brandKey}`);
  };

  const contextValue = {
    routeData: props.routeData,
    onBrandClick,
    ...homeV3Hook,
  };

  // Map of client-specific custom headers
  const customHeaderMap: Record<string, () => JSX.Element> = {
    [clientIdConfig.raisoniClientId]: () => <RaisoniHeader />,
    [clientIdConfig.buddydealsClientId]: () => <BuddydealsHeader />,
    [clientIdConfig.nielsenClientId]: () => <NielsenHeader />,
  };

  // Get custom header component if available
  const CustomHeaderComponent = customHeaderMap[getClientId()];

  // Create interleaved chunks for rendering
  const BRANDS_PER_CHUNK = isMobile() ? 9 : 10;

  const interleavedContent = createMemo(() => {
    const brands = props.routeData()?.brands ?? [];
    const sections = props.routeData()?.sections ?? [];
    const showSeparators = props.routeData()?.showSeparators ?? false;

    type BrandChunk = { type: "brands"; brands: Product[]; isFirst: boolean };
    type SectionChunk = { type: "section"; section: SectionData };
    type ContentChunk = BrandChunk | SectionChunk;

    const chunks: ContentChunk[] = [];
    let brandIndex = 0;
    let sectionIndex = 0;

    // First chunk of brands (with header)
    if (brands.length > 0 && showSeparators) {
      const firstChunk = brands.slice(0, BRANDS_PER_CHUNK);
      chunks.push({ type: "brands", brands: firstChunk, isFirst: true });
      brandIndex = BRANDS_PER_CHUNK;
    }

    // Interleave sections and brand chunks (only chunk while sections available)
    while (showSeparators && sectionIndex < sections.length) {
      // Add section
      chunks.push({ type: "section", section: sections[sectionIndex] });
      sectionIndex++;

      // Add next chunk of brands (only if more sections remain after this)
      if (brandIndex < brands.length && sectionIndex < sections.length) {
        const nextChunk = brands.slice(
          brandIndex,
          brandIndex + BRANDS_PER_CHUNK
        );
        if (nextChunk.length > 0) {
          chunks.push({ type: "brands", brands: nextChunk, isFirst: false });
        }
        brandIndex += BRANDS_PER_CHUNK;
      }
    }

    // All remaining brands go into a single chunk
    if (brandIndex < brands.length) {
      const remainingBrands = brands.slice(brandIndex);
      chunks.push({ type: "brands", brands: remainingBrands, isFirst: false });
    }

    return chunks;
  });

  // Track section color index
  let sectionColorIndex = 0;

  let coinsPillObserver: IntersectionObserver;
  let coinsPillObserverRef: HTMLDivElement | undefined = undefined;

  onMount(() => {
    coinsPillObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsHeaderScrolledOut(false);
      } else {
        setIsHeaderScrolledOut(true);
      }
    });

    if (coinsPillObserverRef && coinsPillObserver) {
      coinsPillObserver.observe(coinsPillObserverRef);
    }

    // Category card sticky detection observer
    categoryObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isAboveViewport = entry.boundingClientRect.top < 112;
        setIsSticky(!entry.isIntersecting && isAboveViewport);
      },
      {
        threshold: 0,
        rootMargin: "-112px 0px 0px 0px",
      }
    );

    if (sentinelRef) {
      categoryObserver.observe(sentinelRef);
    }
  });

  onCleanup(() => {
    if (coinsPillObserver && coinsPillObserverRef) {
      coinsPillObserver.unobserve(coinsPillObserverRef);
    }
    if (categoryObserver && sentinelRef) {
      categoryObserver.unobserve(sentinelRef);
    }
  });

  return (
    <HomeV3Provider value={contextValue}>
      <div class="relative flex min-h-dvh w-full flex-col items-center bg-background-normal-primary">
        {/* Background Gradient */}
        <Show when={getPartnerConfigV3().showGradient}>
          <BackgroundGradientV2
            color={getPartnerConfigV3().gradientColor ?? "#001EFF"}
          />
        </Show>

        {/* Hero Section - Custom or Default */}
        <Show
          when={CustomHeaderComponent}
          fallback={
            <div class="z-[1] flex w-full max-w-[1024px] flex-col items-center px-3 sm:px-0">
              <div class="mt-8"></div>
              {/* Greeting */}
              <h1 class="text-center text-title-6-regular text-text-normal-secondary">
                {getPartnerConfigV3().homepage?.smallTitle ??
                  `Hi ${storeState.v4HomePage?.user?.firstName ?? ""} 👋`}
              </h1>
              <div class="mt-2"></div>
              {/* Main Title */}
              <h2 class="max-w-[570px] text-center text-title-2-semi-bold text-text-normal-primary md:text-display-4-medium">
                {getPartnerConfigV3().homepage?.largeTitle ??
                  `Shop on ${BRANDS_COUNT}+ brands using coins`}
              </h2>
            </div>
          }
        >
          {CustomHeaderComponent!()}
        </Show>

        {/* Coins Balance - Separate from header */}
        <Show
          fallback={<div class="mt-4 sm:mt-8"></div>}
          when={getPartnerConfigV3().showCoins}
        >
          <div class="mt-4">
            <CoinsBalanceDisplay
              balance={storeState.coins ?? 0}
              onClick={() => {
                openModalV2(
                  () => (
                    <CoinsTransactionsModalTelescope
                      balance={storeState.coins ?? 0}
                      userName={storeState.v4HomePage?.user?.firstName ?? ""}
                      refreshV4HomePage={refreshV4HomePage}
                      enableRedeemCode={
                        getPartnerConfigV3().allowCodeApplication
                      }
                      onClose={() => {
                        goBack();
                      }}
                    />
                  ),
                  {
                    expandToFullHeight: true,
                  }
                );
              }}
            />
          </div>
        </Show>

        {/* Coins pill observer ref */}
        <div class="mt-4" ref={coinsPillObserverRef}></div>

        {/* Mobile: Category Card (scrolls with page) */}
        <Show
          when={
            props.routeData()?.categories &&
            props.routeData()?.categories != null &&
            !homeV3Hook.hasLimitedBrands()
          }
        >
          <div class="flex w-full flex-col items-center justify-center px-3 pb-3 pt-3 sm:hidden">
            <div class="w-full max-w-[1024px]">
              <CategoryCardV2
                categories={props.routeData()!.categories.data}
                onBrowseClick={() => {
                  openModalV2(
                    () => (
                      <CategoriesModal
                        categories={props.routeData()!.categories.data}
                      />
                    ),
                    { expandToFullHeight: true }
                  );
                }}
              />
            </div>
          </div>
          {/* Sentinel for sticky detection */}
          <div
            ref={sentinelRef}
            class="h-0 w-full sm:hidden"
            aria-hidden="true"
          />
        </Show>

        {/* Mobile: Sticky Strip (appears when card scrolls out) */}
        <Show
          when={
            props.routeData()?.categories &&
            props.routeData()?.categories != null &&
            !homeV3Hook.hasLimitedBrands()
          }
        >
          <div
            class="fixed top-[112px] w-full flex-col items-center justify-center bg-background-normal-primary px-3 pb-3 pt-3"
            classList={{
              "flex sm:hidden": isSticky(),
              hidden: !isSticky(),
            }}
            style={{ "z-index": zIndexes.categoryStrip }}
          >
            <div class="w-full max-w-[1024px]">
              <CategoryStripV2
                categories={props.routeData()!.categories.data}
                showBrowseFirst={true}
                onBrowseClick={() => {
                  openModalV2(
                    () => (
                      <CategoriesModal
                        categories={props.routeData()!.categories.data}
                      />
                    ),
                    { expandToFullHeight: true }
                  );
                }}
              />
            </div>
          </div>
        </Show>

        {/* Desktop: Always show sticky Strip */}
        <Show
          when={
            props.routeData()?.categories &&
            props.routeData()?.categories != null &&
            !homeV3Hook.hasLimitedBrands()
          }
        >
          <div
            class="sticky top-14 hidden w-full flex-col items-center justify-center bg-background-normal-primary px-0 pb-3 pt-3 sm:flex"
            style={{ "z-index": zIndexes.categoryStrip }}
          >
            <div class="w-full max-w-[1024px]">
              <CategoryStripV2
                categories={props.routeData()!.categories.data}
                showBrowseFirst={true}
                onBrowseClick={() => {
                  openModalV2(
                    () => (
                      <CategoriesModal
                        categories={props.routeData()!.categories.data}
                      />
                    ),
                    { expandToFullHeight: true }
                  );
                }}
              />
            </div>
          </div>
        </Show>

        <Show when={props.routeData()?.showSeparators}>
          <div class="w-full pt-1">
            <PopularBrandsSectionHeader colorIndex={0} />
          </div>
        </Show>

        {/* Brand Sections */}
        <For each={interleavedContent()}>
          {(chunk) => {
            if (chunk.type === "section") {
              const sectionData = chunk.section;
              const currentColorIndex = sectionColorIndex++;

              return (
                <Show
                  when={
                    sectionData.brandList && sectionData.brandList.length > 0
                  }
                >
                  <div class="w-full">
                    {/* Section Header */}
                    <SectionHeader
                      title={sectionData.title}
                      sectionData={sectionData}
                      colorIndex={currentColorIndex}
                      onBrandClick={onBrandClick}
                    />
                  </div>
                </Show>
              );
            } else {
              // Brand chunk
              return (
                <Show when={chunk.brands && chunk.brands.length > 0}>
                  <div class="w-full pb-5 pt-5">
                    <BrandGrid
                      brands={chunk.brands}
                      onBrandClick={onBrandClick}
                    />
                  </div>
                </Show>
              );
            }
          }}
        </For>

        {/* Footer */}
        <Show when={getPartnerConfigV3().showHubbleBranding}>
          <div class="mt-8"></div>
          <div class="fixed bottom-0  flex w-full flex-col gap-2 bg-background-normal-primary px-3 pb-2">
            <div class="h-px w-full bg-stroke-1" />
            <div class="flex w-full flex-col items-center">
              <HubbleBadge />
            </div>
          </div>
        </Show>
      </div>
    </HomeV3Provider>
  );
}
